import React from "react";

import { yaml } from "@codemirror/lang-yaml";
import { oneDark } from "@codemirror/theme-one-dark";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";

import type {
  ManifestDiffResponse,
  ManifestVersionEntry,
} from "./manifest-preview-constants";

interface ManifestDiffViewProps {
  open: boolean;
  onClose: () => void;
  versions: ManifestVersionEntry[];
  diffFrom: number | null;
  diffTo: number | null;
  diffData: ManifestDiffResponse | null;
  diffLoading: boolean;
  diffError: string | null;
  onFromChange: (value: number) => void;
  onToChange: (value: number) => void;
  onFetchDiff: () => void;
}

export function ManifestDiffView({
  open,
  onClose,
  versions,
  diffFrom,
  diffTo,
  diffData,
  diffLoading,
  diffError,
  onFromChange,
  onToChange,
  onFetchDiff,
}: ManifestDiffViewProps): React.ReactElement {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Compare Manifest Versions</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>From</InputLabel>
              <Select
                value={diffFrom ?? ""}
                label="From"
                onChange={(e) => onFromChange(Number(e.target.value))}
              >
                {versions.map((v) => (
                  <MenuItem key={v.version} value={v.version}>
                    v{v.version}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              →
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>To</InputLabel>
              <Select
                value={diffTo ?? ""}
                label="To"
                onChange={(e) => onToChange(Number(e.target.value))}
              >
                {versions.map((v) => (
                  <MenuItem key={v.version} value={v.version}>
                    v{v.version}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="contained"
              onClick={onFetchDiff}
              disabled={diffLoading || diffFrom == null || diffTo == null}
            >
              {diffLoading ? <CircularProgress size={16} /> : "Compare"}
            </Button>
          </Stack>

          {diffError && <Alert severity="error">{diffError}</Alert>}

          {diffData && (
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  v{diffFrom} (from)
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    "& .cm-editor": { fontSize: "0.75rem", maxHeight: 400 },
                    "& .cm-scroller": { maxHeight: 400, overflow: "auto" },
                  }}
                >
                  <CodeMirror
                    value={diffData.from}
                    extensions={[yaml()]}
                    theme={theme.palette.mode === "dark" ? oneDark : undefined}
                    editable={false}
                    readOnly={true}
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: false,
                      highlightActiveLine: false,
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  v{diffTo} (to)
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    "& .cm-editor": { fontSize: "0.75rem", maxHeight: 400 },
                    "& .cm-scroller": { maxHeight: 400, overflow: "auto" },
                  }}
                >
                  <CodeMirror
                    value={diffData.to}
                    extensions={[yaml()]}
                    theme={theme.palette.mode === "dark" ? oneDark : undefined}
                    editable={false}
                    readOnly={true}
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: false,
                      highlightActiveLine: false,
                    }}
                  />
                </Box>
              </Box>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ManifestDiffView;
